import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useLang } from "@/i18n/LangContext";

export default function ForgotPassword() {
  const { lang, setLang, t } = useLang();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim(), lang });
      setSent(true);
    } catch (ex) {
      setErr(fmtErr(ex));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center p-6" data-testid="forgot-password-page">
      <div className="absolute top-4 right-4">
        <div className="inline-flex bg-white/90 backdrop-blur rounded-full p-1 border border-slate-200 shadow-sm">
          <button
            type="button"
            onClick={() => setLang("es")}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${lang === "es" ? "bg-[var(--ink)] text-white" : "text-slate-600"}`}
            data-testid="forgot-lang-es"
          >
            🇪🇸 ES
          </button>
          <button
            type="button"
            onClick={() => setLang("en")}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${lang === "en" ? "bg-[var(--ink)] text-white" : "text-slate-600"}`}
            data-testid="forgot-lang-en"
          >
            🇬🇧 EN
          </button>
        </div>
      </div>

      <div className="w-full max-w-md sns-fade-up">
        <div className="flex justify-center mb-6">
          <img src="/images/logo-brand.png" alt="Smile & Shine" className="h-20 rounded-3xl shadow-lg shadow-slate-300/50" />
        </div>

        {!sent ? (
          <>
            <h2 className="sns-title text-3xl mb-2" data-testid="forgot-title">{t("forgot.title")}</h2>
            <p className="text-slate-500 mb-8">{t("forgot.subtitle")}</p>

            <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
              <div>
                <label className="block text-sm font-semibold mb-2">{t("login.email")}</label>
                <div className="relative">
                  <Mail className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    className="sns-input pl-12"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              {err && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3" data-testid="forgot-error">
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !email}
                className="sns-btn sns-btn-accent w-full text-base py-4 disabled:opacity-60"
                data-testid="forgot-submit-button"
              >
                {loading ? t("forgot.sending") : t("forgot.submit")}
              </button>
            </form>
          </>
        ) : (
          <div className="text-center" data-testid="forgot-success">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mb-4">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h2 className="sns-title text-2xl mb-3">{t("forgot.sent_title")}</h2>
            <p className="text-slate-600 mb-6">{t("forgot.sent_desc")}</p>
            <p className="text-sm text-slate-400 mb-8">{t("forgot.check_spam")}</p>
            <button
              onClick={() => nav("/login")}
              className="sns-btn sns-btn-ghost bg-slate-100 w-full"
              data-testid="forgot-back-login"
            >
              {t("forgot.back_to_login")}
            </button>
          </div>
        )}

        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-slate-500 hover:text-[var(--coral)] inline-flex items-center gap-1" data-testid="forgot-link-back">
            <ArrowLeft className="w-4 h-4" /> {t("forgot.back_to_login")}
          </Link>
        </div>
      </div>
    </div>
  );
}
